/*
 * This controls the Provider profile page
 */

import React, { useEffect, useState } from "react";
import {
  getProvider,
  updateProviderPersonal,
  updateProviderSpecialties,
  unlinkSpecialty,
  unlinkLanguage,
  updateProviderLanguages,
  addEducationRecords,
  updateEducationRecord,
  unlinkEducationRecord,
  addBoardCertifications,
  updateBoardCertification,
  unlinkBoardCertification,
  addFacilities,
  updateFacility,
  unlinkFacility,
  addInsurances,
  unlinkInsurance,
  unclaimProvider,
} from "../APIs/providerApi";
import keycloak from "../APIs/keycloak";
import PersonalInfoEditModal from "./PersonalInfoEditModal";
import SpecialtiesEditModal from "./SpecialtiesEditModal";
import LanguagesEditModal from "./LanguagesEditModal";
import EducationEditModal from "./EducationEditModal";
import BoardCertificationEditModal from "./BoardCertificationEditModal";
import FacilityEditModal from "./FacilityEditModal";
import InsuranceAddModal from "./InsuranceAddModal";

// --- small helpers ---
const fmtDate = (d) => {
  if (!d) return "N/A";
  const dt = new Date(d);
  return Number.isNaN(dt.getTime()) ? d : dt.toISOString().slice(0, 10);
};

const Section = ({ title, children, right }) => (
  <div className="rounded-xl border bg-white p-5 md:p-6 shadow-sm">
    <div className="mb-3 flex items-center justify-between">
      <h3 className="text-base md:text-lg font-semibold text-gray-900">
        {title}
      </h3>
      {right}
    </div>
    {children}
  </div>
);

const ProviderInformation = ({ providerId }) => {
  const [provider, setProvider] = useState(null);
  const [loading, setLoading] = useState(Boolean(providerId));
  const [error, setError] = useState(null);
  const [unclaiming, setUnclaiming] = useState(false);
  const [showAddLangs, setShowAddLangs] = useState(false);
  const [busyLangId, setBusyLangId] = useState(null);
  // state for specialties
  const [showAddSpecs, setShowAddSpecs] = useState(false);
  const [busySpecId, setBusySpecId] = useState(null); // for per-item delete spinner

  //state for education
  const [showAddEdu, setShowAddEdu] = useState(false);
  const [editEdu, setEditEdu] = useState(null); // holds the record being edited (or null)
  const [busyEduId, setBusyEduId] = useState(null); // per-item deleting/updating indicator

  //state for boardCertification
  const [showAddCert, setShowAddCert] = useState(false);
  const [editCert, setEditCert] = useState(null); // holds the record being edited
  const [busyCertId, setBusyCertId] = useState(null); // per-item spinner/disable

  //state for facilityInfomration
  const [showAddFacility, setShowAddFacility] = useState(false);
  const [editFacility, setEditFacility] = useState(null);
  const [busyFacilityId, setBusyFacilityId] = useState(null);

  //state for insurance
  const [showAddInsurance, setShowAddInsurance] = useState(false);
  const [busyPlanId, setBusyPlanId] = useState(null);

  //For insurance
  //const insurances = provider?.insurances ?? []; // from ProviderSignUpResponseDTO

  // ADD: merge one row via main PUT
  const handleAddInsurance = async ({ planName, planType }) => {
    const updated = await addInsurances(provider.providerId, [
      { planName, planType },
    ]);
    setProvider(updated);
    setShowAddInsurance(false);
  };

  // DELETE: uses planId from DTO
  const handleDeleteInsurance = async (ins) => {
    const planId = ins?.planId;
    if (!planId) {
      alert("Cannot delete: missing planId on provider insurance row.");
      return;
    }
    setBusyPlanId(planId);
    try {
      const updated = await unlinkInsurance(provider.providerId, planId);
      setProvider(updated);
    } finally {
      setBusyPlanId(null);
    }
  };

  // modal state
  const [showEditPersonal, setShowEditPersonal] = useState(false);

  useEffect(() => {
    let alive = true;
    (async () => {
      if (!providerId) {
        setLoading(false);
        return;
      }
      try {
        setLoading(true);
        const data = await getProvider(providerId);
        if (alive) setProvider(data);
      } catch (e) {
        if (alive) setError(e?.message || "Failed to load provider");
      } finally {
        if (alive) setLoading(false);
      }
    })();
    return () => {
      alive = false;
    };
  }, [providerId]);

  // null-safe locals
  const specialties = provider?.specialties ?? null;
  const languages = provider?.languages ?? null;
  const insurances = provider?.insurances ?? null;
  const educationRecords = provider?.educationRecords ?? null;
  const boardCertifications = provider?.boardCertifications ?? null;

  if (loading) {
    return (
      <div className="rounded-xl border bg-white p-6 shadow-sm">
        <p className="text-gray-500">Loading provider…</p>
      </div>
    );
  }
  if (error) {
    return (
      <div className="rounded-xl border bg-white p-6 shadow-sm">
        <p className="text-red-600">Error: {error}</p>
      </div>
    );
  }
  if (!provider) {
    return (
      <div className="rounded-xl border bg-white p-6 shadow-sm">
        <p className="text-gray-500">No provider selected.</p>
      </div>
    );
  }

  // Save handler for the modal
  /*const handleSavePersonal = async (values) => {
    // Build a full form payload from the current provider, then override the personal fields with `values`
    const form = buildFormFromProvider(provider);
    form.firstName = values.firstName ?? null;
    form.lastName = values.lastName ?? null;
    form.dateOfBirth = values.dateOfBirth ?? null;
    form.email = values.email ?? null;
    form.phoneNumber = values.phoneNumber ?? null;
    form.gender = values.gender ?? null;
    form.ethnicity = values.ethnicity ?? null;
    form.yearsOfExperience = values.yearsOfExperience ?? null;

    // PUT (multipart/form-data) via updateProvider -> formatProviderPayload
    const updated = await updateProvider(provider.providerId, form);
    // Update local state with fresh DTO from server
    setProvider(updated);
    setShowEditPersonal(false);
  };*/

  // Save handler for the modal (REPLACE THIS WHOLE FUNCTION)
  const handleSavePersonal = async (values) => {
    // send ONLY personal fields, matching your working Postman shape
    const payload = {
      firstName: values.firstName ?? null,
      lastName: values.lastName ?? null,
      dateOfBirth: values.dateOfBirth ?? null, // "YYYY-MM-DD"
      email: values.email ?? null,
      phoneNumber: values.phoneNumber ?? null,
      gender: values.gender ?? null,
      ethnicity: values.ethnicity ?? null,
      yearsOfExperience:
        values.yearsOfExperience === "" || values.yearsOfExperience == null
          ? null
          : Number(values.yearsOfExperience),
    };

    const updated = await updateProviderPersonal(provider.providerId, payload);
    setProvider(updated); // server returns the full DTO
    setShowEditPersonal(false);
  };

  // helpers to read specialties consistently
  //const specialties = provider?.specialties ?? [];

  // ADD handler (merge)
  const handleAddSpecialties = async (names) => {
    const updated = await updateProviderSpecialties(provider.providerId, names);
    setProvider(updated);
    setShowAddSpecs(false);
  };

  // DELETE handler
  const handleDeleteSpecialty = async (spec) => {
    // Try to find an id on the item. It might be `id` or `specialtyId`.
    const specId = spec?.id ?? spec?.specialtyId;
    if (!specId) {
      alert("Cannot delete: specialty id missing on provider data.");
      return;
    }
    try {
      setBusySpecId(specId);
      const updated = await unlinkSpecialty(provider.providerId, specId);
      setProvider(updated);
    } finally {
      setBusySpecId(null);
    }
  };

  //Languages
  // languages array from DTO; each item like { languageId, languageName }
  //const languages = provider?.languages ?? [];

  // ADD handler (merge by names)
  const handleAddLanguages = async (names) => {
    const updated = await updateProviderLanguages(provider.providerId, names);
    setProvider(updated);
    setShowAddLangs(false);
  };

  // DELETE handler (by id)
  const handleDeleteLanguage = async (lang) => {
    const langId = lang?.languageId ?? lang?.id;
    if (!langId) {
      alert("Cannot delete: language id missing on provider data.");
      return;
    }
    try {
      setBusyLangId(langId);
      const updated = await unlinkLanguage(provider.providerId, langId);
      setProvider(updated);
    } finally {
      setBusyLangId(null);
    }
  };

  //For educational records
  //const educationRecords = provider?.educationRecords ?? [];

  // ADD — uses merge PUT with minimal payload
  const handleAddEducation = async (values) => {
    // values: { institutionName, degree, fieldOfStudy, startYear, endYear }
    const updated = await addEducationRecords(provider.providerId, [values]);
    setProvider(updated);
    setShowAddEdu(false);
  };

  // EDIT — calls JSON PUT /{providerId}/education/{educationId}
  const handleSaveEditEducation = async (educationId, values) => {
    setBusyEduId(educationId);
    try {
      const updated = await updateEducationRecord(
        provider.providerId,
        educationId,
        values,
      );
      setProvider(updated);
      setEditEdu(null);
    } finally {
      setBusyEduId(null);
    }
  };

  // DELETE — calls DELETE /{providerId}/education/{educationId}/unlink
  const handleDeleteEducation = async (edu) => {
    const id = edu?.educationId ?? edu?.id;
    if (!id) {
      alert("Cannot delete: educationId missing.");
      return;
    }
    setBusyEduId(id);
    try {
      const updated = await unlinkEducationRecord(provider.providerId, id);
      setProvider(updated);
    } finally {
      setBusyEduId(null);
    }
  };

  //Helpers for boardCertification
  //const boardCertifications = provider?.boardCertifications ?? [];

  // ADD — merge with main PUT
  const handleAddBoardCertification = async (values) => {
    const updated = await addBoardCertifications(provider.providerId, [values]);
    setProvider(updated);
    setShowAddCert(false);
  };

  // EDIT — prefer dedicated endpoint; fallback handled inside helper
  const handleSaveEditBoardCertification = async (certificationId, values) => {
    setBusyCertId(certificationId);
    try {
      const updated = await updateBoardCertification(
        provider.providerId,
        certificationId,
        values,
      );
      setProvider(updated);
      setEditCert(null);
    } finally {
      setBusyCertId(null);
    }
  };

  // DELETE (unlink)
  const handleDeleteBoardCertification = async (cert) => {
    const id = cert?.certificationId ?? cert?.id;
    if (!id) {
      alert("Cannot delete: certification id missing.");
      return;
    }
    setBusyCertId(id);
    try {
      const updated = await unlinkBoardCertification(provider.providerId, id);
      setProvider(updated);
    } finally {
      setBusyCertId(null);
    }
  };

  //Helper for facility
  const facilities = provider?.facilityInformation ?? []; // from GET response

  // ADD
  /*const handleAddFacility = async (values) => {
      const updated = await addFacilities(provider.providerId, [values]);
      setProvider(updated);
      setShowAddFacility(false);
    };*/
  const handleAddFacility = async (values) => {
    // values from modal: { facilityName, facilityEmail, facilityWebsite, addressLine, city, state, country }
    const org = {
      facilityName: values.facilityName || null,
      facilityEmail: values.facilityEmail || null,
      facilityWebsite: values.facilityWebsite || null,
    };

    const addresses = [
      {
        addressLine: values.addressLine || null,
        city: values.city || null,
        state: values.state || null,
        country: values.country || "United States",
      },
    ];

    const updated = await addFacilities(provider.providerId, addresses, org);
    setProvider(updated);
    setShowAddFacility(false);
  };

  // EDIT
  const handleSaveEditFacility = async (values) => {
    const locationId =
      values.locationId ?? editFacility?.locationId ?? editFacility?.id;
    if (!locationId) {
      alert("Missing locationId for facility edit.");
      return;
    }
    setBusyFacilityId(locationId);
    try {
      const updated = await updateFacility(
        provider.providerId,
        locationId,
        values,
      );
      setProvider(updated);
      setEditFacility(null);
    } finally {
      setBusyFacilityId(null);
    }
  };

  // DELETE
  const handleDeleteFacility = async (fac) => {
    const id = fac?.locationId ?? fac?.id;
    if (!id) return alert("Cannot delete: missing locationId");
    setBusyFacilityId(id);
    try {
      const updated = await unlinkFacility(provider.providerId, id);
      setProvider(updated);
    } finally {
      setBusyFacilityId(null);
    }
  };

  const handleUnclaim = async () => {
    if (
      !window.confirm(
        "Are you sure you want to unclaim this profile? You will be logged out immediately.",
      )
    )
      return;
    setUnclaiming(true);
    try {
      await unclaimProvider(providerId);
      localStorage.removeItem("medmaze_signup_role_locked");
      localStorage.removeItem("medmaze_signup_role_pending");
      localStorage.removeItem("medmaze_pending_claim_npi");
      keycloak.logout({
        redirectUri: window.location.origin + "/ProviderSearchPage",
      });
    } catch (e) {
      alert(
        e?.response?.data?.message ||
          e?.message ||
          "Failed to unclaim profile.",
      );
      setUnclaiming(false);
    }
  };

  return (
    <>
      <div className="space-y-5">
        {/* Personal Information */}
        <Section
          title="Personal Information"
          right={
            <button
              onClick={() => setShowEditPersonal(true)}
              className="rounded bg-emerald-600 px-3 py-1.5 text-white text-sm hover:bg-emerald-700"
            >
              Edit
            </button>
          }
        >
          <div className="grid grid-cols-1 md:grid-cols-2 gap-y-2">
            <div className="space-y-1">
              <p className="text-sm text-gray-500">
                <span className="font-medium text-gray-700">First Name:</span>{" "}
                <span className="text-gray-900">
                  {provider.firstName || "N/A"}
                </span>
              </p>
              <p className="text-sm text-gray-500">
                <span className="font-medium text-gray-700">DOB:</span>{" "}
                <span className="text-gray-900">
                  {fmtDate(provider.dateOfBirth)}
                </span>
              </p>
              <p className="text-sm text-gray-500">
                <span className="font-medium text-gray-700">Phone:</span>{" "}
                <span className="text-gray-900">
                  {provider.phoneNumber || "N/A"}
                </span>
              </p>
              <p className="text-sm text-gray-500">
                <span className="font-medium text-gray-700">Fax:</span>{" "}
                <span className="text-gray-900">
                  {provider.faxNumber || "N/A"}
                </span>
              </p>
              <p className="text-sm text-gray-500">
                <span className="font-medium text-gray-700">Ethnicity:</span>{" "}
                <span className="text-gray-900">
                  {provider.ethnicity || "N/A"}
                </span>
              </p>
            </div>
            <div className="space-y-1">
              <p className="text-sm text-gray-500">
                <span className="font-medium text-gray-700">Last Name:</span>{" "}
                <span className="text-gray-900">
                  {provider.lastName || "N/A"}
                </span>
              </p>
              <p className="text-sm text-gray-500">
                <span className="font-medium text-gray-700">Email:</span>{" "}
                <span className="text-gray-900">{provider.email || "N/A"}</span>
              </p>
              <p className="text-sm text-gray-500">
                <span className="font-medium text-gray-700">Gender:</span>{" "}
                <span className="text-gray-900">
                  {provider.gender || "N/A"}
                </span>
              </p>
              <p className="text-sm text-gray-500">
                <span className="font-medium text-gray-700">
                  Years of Experience:
                </span>{" "}
                <span className="text-gray-900">
                  {provider.yearsOfExperience ?? "N/A"}
                </span>
              </p>
            </div>
          </div>
        </Section>

        {/* The rest of your read-only sections remain unchanged */}
        <Section
          title="Specialties"
          right={
            <div className="space-x-2">
              <button
                onClick={() => setShowAddSpecs(true)}
                className="rounded bg-emerald-600 px-3 py-1.5 text-white text-sm hover:bg-emerald-700"
              >
                Add New
              </button>
            </div>
          }
        >
          {!Array.isArray(specialties) || specialties.length === 0 ? (
            <p className="text-gray-500">No specialties on file.</p>
          ) : (
            <ul className="space-y-2">
              {specialties.map((s, i) => {
                const name = typeof s === "string" ? s : (s?.name ?? "N/A");
                const id = s?.id ?? s?.specialtyId ?? null;
                return (
                  <li
                    key={id ?? i}
                    className="flex items-center justify-between rounded border p-3"
                  >
                    <span className="text-gray-900">{name}</span>
                    <button
                      disabled={!id || busySpecId === id}
                      onClick={() => handleDeleteSpecialty(s)}
                      className="text-sm text-red-600 hover:underline disabled:opacity-50"
                      title={id ? "Remove specialty" : "Missing id"}
                    >
                      {busySpecId === id ? "Removing…" : "Delete"}
                    </button>
                  </li>
                );
              })}
            </ul>
          )}
        </Section>

        {showAddSpecs && (
          <SpecialtiesEditModal
            initialSelectedNames={
              Array.isArray(specialties)
                ? specialties
                    .map((s) => (typeof s === "string" ? s : s?.name))
                    .filter(Boolean)
                : []
            }
            onClose={() => setShowAddSpecs(false)}
            onSave={handleAddSpecialties}
          />
        )}

        <Section
          title="Languages Spoken"
          right={
            <button
              onClick={() => setShowAddLangs(true)}
              className="rounded bg-emerald-600 px-3 py-1.5 text-white text-sm hover:bg-emerald-700"
            >
              Add New
            </button>
          }
        >
          {!Array.isArray(languages) || languages.length === 0 ? (
            <p className="text-gray-500">No languages on file.</p>
          ) : (
            <ul className="space-y-2">
              {languages.map((l, i) => {
                const name = l?.languageName ?? "N/A";
                const id = l?.languageId ?? l?.id ?? null;
                return (
                  <li
                    key={id ?? i}
                    className="flex items-center justify-between rounded border p-3"
                  >
                    <span className="text-gray-900">{name}</span>
                    <button
                      disabled={!id || busyLangId === id}
                      onClick={() => handleDeleteLanguage(l)}
                      className="text-sm text-red-600 hover:underline disabled:opacity-50"
                      title={id ? "Remove language" : "Missing id"}
                    >
                      {busyLangId === id ? "Removing…" : "Delete"}
                    </button>
                  </li>
                );
              })}
            </ul>
          )}
        </Section>

        {showAddLangs && (
          <LanguagesEditModal
            initialSelectedNames={
              Array.isArray(languages)
                ? languages.map((l) => l?.languageName).filter(Boolean)
                : []
            }
            onClose={() => setShowAddLangs(false)}
            onSave={handleAddLanguages}
          />
        )}

        <Section
          title="Education Records"
          right={
            <button
              onClick={() => setShowAddEdu(true)}
              className="rounded bg-emerald-600 px-3 py-1.5 text-white text-sm hover:bg-emerald-700"
            >
              Add New
            </button>
          }
        >
          {!Array.isArray(educationRecords) || educationRecords.length === 0 ? (
            <p className="text-gray-500">No education records on file.</p>
          ) : (
            <ul className="space-y-3">
              {educationRecords.map((e, i) => {
                const id = e?.educationId ?? e?.id ?? i;
                return (
                  <li key={id} className="rounded border p-4">
                    <div className="flex items-start justify-between gap-3">
                      <div>
                        <div className="font-medium">
                          {e?.institutionName ?? "N/A"}
                        </div>
                        <div className="text-sm text-gray-600">
                          <span className="font-medium text-gray-700">
                            Degree:
                          </span>{" "}
                          {e?.degree ?? "N/A"}
                          {" · "}
                          <span className="font-medium text-gray-700">
                            Field of Study:
                          </span>{" "}
                          {e?.fieldOfStudy ?? "N/A"}
                          {" · "}
                          <span className="font-medium text-gray-700">
                            Years:
                          </span>{" "}
                          {(e?.startYear ?? "N/A") +
                            " - " +
                            (e?.endYear ?? "N/A")}
                        </div>
                      </div>
                      <div className="shrink-0 space-x-3">
                        <button
                          onClick={() => setEditEdu(e)}
                          className="text-sm text-emerald-700 hover:underline"
                        >
                          Edit
                        </button>
                        <button
                          disabled={busyEduId === id || !e?.educationId}
                          onClick={() => handleDeleteEducation(e)}
                          className="text-sm text-red-600 hover:underline disabled:opacity-50"
                        >
                          {busyEduId === id ? "Removing…" : "Delete"}
                        </button>
                      </div>
                    </div>
                  </li>
                );
              })}
            </ul>
          )}
        </Section>

        {/* Add modal */}
        {showAddEdu && (
          <EducationEditModal
            mode="add"
            onClose={() => setShowAddEdu(false)}
            onSave={handleAddEducation}
          />
        )}

        {/* Edit modal */}
        {editEdu && (
          <EducationEditModal
            mode="edit"
            education={editEdu}
            onClose={() => setEditEdu(null)}
            onSave={(vals) =>
              handleSaveEditEducation(editEdu.educationId, vals)
            }
          />
        )}

        <Section
          title="Board Certifications"
          right={
            <button
              onClick={() => setShowAddCert(true)}
              className="rounded bg-emerald-600 px-3 py-1.5 text-white text-sm hover:bg-emerald-700"
            >
              Add New
            </button>
          }
        >
          {!Array.isArray(boardCertifications) ||
          boardCertifications.length === 0 ? (
            <p className="text-gray-500">No board certifications on file.</p>
          ) : (
            <ul className="space-y-3">
              {boardCertifications.map((c, i) => {
                const id = c?.certificationId ?? c?.id ?? i;
                return (
                  <li key={id} className="rounded border p-4">
                    <div className="flex items-start justify-between gap-3">
                      <div>
                        <div className="font-medium">{c?.name ?? "N/A"}</div>
                        <div className="text-sm text-gray-600">
                          <span className="font-medium text-gray-700">
                            Number:
                          </span>{" "}
                          {c?.certificationNumber ?? "N/A"}
                          {" · "}
                          <span className="font-medium text-gray-700">
                            Issuing Body:
                          </span>{" "}
                          {c?.issuingBody ?? "N/A"}
                          {" · "}
                          <span className="font-medium text-gray-700">
                            Status:
                          </span>{" "}
                          {c?.status ?? "N/A"}
                        </div>
                        <div className="text-xs text-gray-500 mt-1">
                          <span className="font-medium text-gray-600">
                            Certified:
                          </span>{" "}
                          {c?.certifiedOn ?? "N/A"}
                          {" · "}
                          <span className="font-medium text-gray-600">
                            Expires:
                          </span>{" "}
                          {c?.expiresOn ?? "N/A"}
                          {" · "}
                          <span className="font-medium text-gray-600">
                            Renewal:
                          </span>{" "}
                          {c?.requiresRenewal ? "Yes" : "No"}
                          {" · "}
                          <span className="font-medium text-gray-600">
                            Verified:
                          </span>{" "}
                          {c?.isVerified ? "Yes" : "No"}
                        </div>
                      </div>
                      <div className="shrink-0 space-x-3">
                        <button
                          onClick={() => setEditCert(c)}
                          className="text-sm text-emerald-700 hover:underline"
                        >
                          Edit
                        </button>
                        <button
                          disabled={busyCertId === id || !c?.certificationId}
                          onClick={() => handleDeleteBoardCertification(c)}
                          className="text-sm text-red-600 hover:underline disabled:opacity-50"
                        >
                          {busyCertId === id ? "Removing…" : "Delete"}
                        </button>
                      </div>
                    </div>
                  </li>
                );
              })}
            </ul>
          )}
        </Section>

        {/* Add modal */}
        {showAddCert && (
          <BoardCertificationEditModal
            mode="add"
            onClose={() => setShowAddCert(false)}
            onSave={handleAddBoardCertification}
          />
        )}

        {/* Edit modal */}
        {editCert && (
          <BoardCertificationEditModal
            mode="edit"
            certification={editCert}
            onClose={() => setEditCert(null)}
            onSave={(vals) =>
              handleSaveEditBoardCertification(editCert.certificationId, vals)
            }
          />
        )}

        <Section
          title="Facility Information"
          right={
            <button
              onClick={() => setShowAddFacility(true)}
              className="rounded bg-emerald-600 px-3 py-1.5 text-white text-sm hover:bg-emerald-700"
            >
              Add New
            </button>
          }
        >
          {!Array.isArray(facilities) || facilities.length === 0 ? (
            <p className="text-gray-500">No facilities on file.</p>
          ) : (
            <ul className="space-y-3">
              {facilities.map((f, i) => {
                const id = f?.locationId ?? f?.id ?? i;
                return (
                  <li key={id} className="rounded border p-4">
                    <div className="flex items-start justify-between gap-3">
                      <div>
                        <div className="flex items-center gap-2 font-medium">
                          {f?.facilityName ?? "N/A"}
                          {f?.addressType && (
                            <span
                              className={`text-xs px-2 py-0.5 rounded-full font-semibold ${
                                f.addressType === "PRIMARY"
                                  ? "bg-teal-100 text-teal-700"
                                  : "bg-gray-100 text-gray-600"
                              }`}
                            >
                              {f.addressType}
                            </span>
                          )}
                        </div>
                        <div className="text-sm text-gray-600">
                          <span className="font-medium text-gray-700">
                            Email:
                          </span>{" "}
                          {f?.facilityEmail ?? "N/A"}
                          {" · "}
                          <span className="font-medium text-gray-700">
                            Website:
                          </span>{" "}
                          {f?.facilityWebsite ?? "N/A"}
                        </div>
                        <div className="text-sm text-gray-600 mt-1">
                          {[
                            f?.addressLine || "N/A",
                            f?.city || "N/A",
                            f?.state || "N/A",
                            f?.country || "N/A",
                          ]
                            .filter(Boolean)
                            .join(", ")}
                        </div>
                        {f?.phoneNumber && (
                          <div className="text-sm text-gray-600 mt-1">
                            <span className="font-medium text-gray-700">
                              Phone:
                            </span>{" "}
                            {f.phoneNumber}
                          </div>
                        )}
                      </div>
                      <div className="shrink-0 space-x-3">
                        <button
                          onClick={() => setEditFacility(f)}
                          className="text-sm text-emerald-700 hover:underline"
                        >
                          Edit
                        </button>
                        <button
                          disabled={busyFacilityId === id || !f?.locationId}
                          onClick={() => handleDeleteFacility(f)}
                          className="text-sm text-red-600 hover:underline disabled:opacity-50"
                        >
                          {busyFacilityId === id ? "Removing…" : "Delete"}
                        </button>
                      </div>
                    </div>
                  </li>
                );
              })}
            </ul>
          )}
        </Section>

        {/* Add modal */}
        {showAddFacility && (
          <FacilityEditModal
            mode="add"
            onClose={() => setShowAddFacility(false)}
            onSave={handleAddFacility}
          />
        )}

        {/* Edit modal */}
        {editFacility && (
          <FacilityEditModal
            mode="edit"
            facility={editFacility}
            onClose={() => setEditFacility(null)}
            onSave={handleSaveEditFacility}
          />
        )}

        {/* Insurance Modal */}
        <Section
          title="Insurance Accepted"
          right={
            <button
              onClick={() => setShowAddInsurance(true)}
              className="rounded bg-emerald-600 px-3 py-1.5 text-white text-sm hover:bg-emerald-700"
            >
              Add New
            </button>
          }
        >
          {!Array.isArray(insurances) || insurances.length === 0 ? (
            <p className="text-gray-500">No insurance on file.</p>
          ) : (
            <ul className="space-y-2">
              {insurances.map((ins, idx) => (
                <li
                  key={ins.planId ?? idx}
                  className="flex items-center justify-between rounded border p-3"
                >
                  <div className="text-sm">
                    <div className="font-medium">{ins.planName ?? "N/A"}</div>
                    <div className="text-gray-600">{ins.planType ?? "N/A"}</div>
                  </div>
                  <button
                    className="text-sm text-red-600 hover:underline disabled:opacity-50"
                    disabled={!ins.planId || busyPlanId === ins.planId}
                    onClick={() => handleDeleteInsurance(ins)}
                  >
                    {busyPlanId === ins.planId ? "Removing…" : "Delete"}
                  </button>
                </li>
              ))}
            </ul>
          )}
        </Section>

        {showAddInsurance && (
          <InsuranceAddModal
            onClose={() => setShowAddInsurance(false)}
            onSave={handleAddInsurance}
          />
        )}

        {/* Unclaim Profile */}
        <div className="pt-6 border-t mt-6 flex justify-center">
          <button
            onClick={handleUnclaim}
            disabled={unclaiming}
            className="rounded bg-red-600 px-4 py-2 text-white text-sm hover:bg-red-700 disabled:opacity-50"
          >
            {unclaiming ? "Unclaiming…" : "Unclaim Profile"}
          </button>
        </div>
      </div>

      {/* Edit Personal Modal */}
      {showEditPersonal && (
        <PersonalInfoEditModal
          initial={{
            firstName: provider.firstName ?? "",
            lastName: provider.lastName ?? "",
            dateOfBirth: provider.dateOfBirth
              ? fmtDate(provider.dateOfBirth)
              : "",
            email: provider.email ?? "",
            phoneNumber: provider.phoneNumber ?? "",
            gender: provider.gender ?? "",
            ethnicity: provider.ethnicity ?? "",
            yearsOfExperience: provider.yearsOfExperience ?? "",
          }}
          onClose={() => setShowEditPersonal(false)}
          onSave={handleSavePersonal}
        />
      )}
    </>
  );
};

export default ProviderInformation;
